import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, MapPin, Phone, Twitter, Youtube } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT, DISCLAIMER, SERVICES } from "../../lib/data";

const QUICK = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Loan Solutions", path: "/solutions" },
  { label: "Why LendX", path: "/why-lendx" },
  { label: "Contact Us", path: "/contact" },
];

const FOOTER_SERVICES = ["home-loan", "loan-against-property", "business-loan", "working-capital-finance", "commercial-property-finance", "corporate-finance"];

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com" },
  { icon: Twitter, label: "Twitter / X", href: "https://x.com" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com" },
  { icon: Youtube, label: "YouTube", href: "https://www.youtube.com" },
];

export const Footer = () => (
  <footer className="border-t border-gray-200 bg-fog" data-testid="main-footer">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <Link to="/contact" data-testid="footer-lets-talk" className="group block overflow-hidden py-16 md:py-20">
        <span className="block whitespace-nowrap font-heading text-[13vw] font-bold leading-none tracking-tighter text-outline transition-all duration-700 group-hover:text-outline-teal lg:text-[10vw]">
          LET'S TALK
        </span>
      </Link>

      <div className="grid gap-12 border-t border-gray-200 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-gray-600">
            Professional financial guidance and structured financing solutions for individuals and businesses.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors duration-300 hover:border-orange hover:text-orange-deep"
                >
                  <Icon size={15} />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gray-400">Quick Links</h4>
          <ul className="mt-5 space-y-3">
            {QUICK.map((l) => (
              <li key={l.path}>
                <Link to={l.path} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-gray-600 transition-colors duration-300 hover:text-orange-deep">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gray-400">Loan Solutions</h4>
          <ul className="mt-5 space-y-3">
            {FOOTER_SERVICES.map((slug) => {
              const s = SERVICES.find((x) => x.slug === slug);
              return (
                <li key={slug}>
                  <Link to={`/solutions/${slug}`} data-testid={`footer-service-${slug}`} className="text-sm text-gray-600 transition-colors duration-300 hover:text-orange-deep">
                    {s.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gray-400">Contact</h4>
          <ul className="mt-5 space-y-3 text-sm text-gray-600">
            <li>
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-2 transition-colors hover:text-orange-deep">
                <Phone size={13} className="text-gold-deep" /> {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={CONTACT.landlineHref} data-testid="footer-landline" className="flex items-center gap-2 transition-colors hover:text-orange-deep">
                <Phone size={13} className="text-gold-deep" /> {CONTACT.landline}
              </a>
            </li>
            <li>
              <a href={CONTACT.emailHref} data-testid="footer-email" className="flex items-center gap-2 transition-colors hover:text-orange-deep">
                <Mail size={13} className="text-gold-deep" /> {CONTACT.email}
              </a>
            </li>
            <li>
              <a href={CONTACT.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-address" className="flex items-start gap-2 transition-colors hover:text-orange-deep">
                <MapPin size={13} className="mt-1 shrink-0 text-gold-deep" />
                <span className="text-xs leading-relaxed">{CONTACT.address}</span>
              </a>
            </li>
            <li className="text-gray-600">{CONTACT.website}</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-gray-200 py-8">
        <p className="text-xs leading-relaxed text-gray-500" data-testid="footer-disclaimer">{DISCLAIMER}</p>
      </div>

      <div className="flex flex-col items-center justify-between gap-4 border-t border-gray-200 py-7 md:flex-row">
        <p className="text-xs text-gray-400">© {new Date().getFullYear()} LendX. All rights reserved.</p>
        <div className="flex gap-6">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-xs text-gray-400 transition-colors hover:text-orange-deep">Privacy Policy</Link>
          <Link to="/terms-and-conditions" data-testid="footer-terms-link" className="text-xs text-gray-400 transition-colors hover:text-orange-deep">Terms &amp; Conditions</Link>
          <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="text-xs text-gray-400 transition-colors hover:text-orange-deep">Disclaimer</Link>
        </div>
      </div>
    </div>
  </footer>
);
